/**
 * Room-scoped date/time resolution.
 *
 * Every environment runs its containers in UTC with no `TZ` set, so a bare
 * `new Date().toLocaleDateString()` resolves against UTC. Between local
 * midnight-minus-offset and UTC midnight that is a *different calendar day*
 * from the one the room is actually in — which is how a companion ends up
 * insisting it is tomorrow. Everything the AI says about "today" must go
 * through here with the room's own zone.
 */

/** Fallback when a room has no zone, or carries one this runtime cannot resolve. */
export const DEFAULT_ROOM_TIMEZONE = 'UTC';

/**
 * True if the runtime can actually resolve `timeZone` as an IANA zone.
 * `Intl.DateTimeFormat` throws a RangeError on unknown zones, so this is the
 * only reliable check — there is no lookup table to compare against.
 */
export const isValidTimeZone = (timeZone: string): boolean => {
    if (!timeZone) return false;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone });
        return true;
    } catch {
        return false;
    }
};

/**
 * Coerce a persisted room timezone into one this runtime can format with.
 * Never throws — a room row carrying a zone that a newer/older ICU does not
 * know must not take a turn down with it.
 */
export const resolveRoomTimeZone = (timeZone?: string | null): string => (
    timeZone && isValidTimeZone(timeZone) ? timeZone : DEFAULT_ROOM_TIMEZONE
);

/** The pieces of "now" a prompt needs, all resolved in one zone. */
export interface RoomDateContext {
    /** Resolved IANA zone actually used (may be the fallback). */
    timeZone: string;
    /** `YYYY-MM-DD` in the room's zone. */
    iso: string;
    /** e.g. `Thursday, September 3, 2026` in the room's zone. */
    formatted: string;
    /** Calendar year in the room's zone — for year-qualifying search queries. */
    year: number;
    /** Wall-clock time in the room's zone with its abbreviation, e.g. `9:03 PM EDT`. */
    time: string;
}

/**
 * Build every date string a prompt needs, resolved in the room's timezone.
 *
 * Note the ISO date is assembled from `formatToParts` rather than
 * `toISOString().split('T')[0]` — `toISOString` is *always* UTC and silently
 * ignores the zone, which is the original bug.
 */
export const getRoomDateContext = (
    timeZone?: string | null,
    now: Date = new Date(),
): RoomDateContext => {
    const zone = resolveRoomTimeZone(timeZone);

    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(now);

    const part = (type: Intl.DateTimeFormatPartTypes): string => (
        parts.find((p) => p.type === type)?.value ?? ''
    );

    const year = part('year');
    const iso = `${year}-${part('month')}-${part('day')}`;

    const formatted = new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    }).format(now);

    const time = new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
    }).format(now);

    return { timeZone: zone, iso, formatted, year: Number(year), time };
};
